import type { FormEvent, ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { colors } from '../../../../styles/colors';
import { OnboardingLayoutContextProvider, type OnboardingLayoutContextValue } from './OnboardingLayoutContext';

export type OnboardingLayoutProps = {
  currentStep: number;
  totalSteps: number;
  children: ReactNode;
  /** Defaults to SPA back navigation when history allows. */
  onBack?: () => void;
  /** Defaults to no-op until screens wire navigation. */
  onForward?: () => void;
  /** Default primary CTA label in the footer. */
  forwardLabel?: ReactNode;
  /** Blocks form submit and disables the primary footer CTA. */
  forwardDisabled?: boolean;
  /** Dims the primary CTA to look disabled while keeping it clickable (submit still fires). */
  forwardInactive?: boolean;
  /** Hides the primary footer CTA and blocks Enter-to-submit. */
  hideForward?: boolean;
  /** Sets `aria-busy` on the primary footer CTA during async work. */
  forwardBusy?: boolean;
};

const Root = styled.form`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  background: ${colors.background};
`;

/**
 * Root shell for onboarding screens: `<form>` host with Enter-to-submit, layout context (steps,
 * footer actions). Compose with {@link OnboardingHeader}, {@link OnboardingBody}, {@link OnboardingFooter}
 * in that order. Wire `onForward`, `forwardDisabled`, and `hideForward` on this component.
 */
export function OnboardingLayout({
  currentStep,
  totalSteps,
  children,
  onBack: onBackProp,
  onForward: onForwardProp,
  forwardLabel = 'Weiter',
  forwardDisabled = false,
  forwardInactive = false,
  hideForward = false,
  forwardBusy = false,
}: OnboardingLayoutProps) {
  const navigate = useNavigate();

  const defaultOnBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const defaultOnForward = useCallback(() => {}, []);

  const onBack = onBackProp ?? defaultOnBack;
  const onForward = onForwardProp ?? defaultOnForward;

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (hideForward || forwardDisabled) {
        return;
      }
      onForward();
    },
    [hideForward, forwardDisabled, onForward],
  );

  const value: OnboardingLayoutContextValue = useMemo(
    () => ({
      currentStep,
      totalSteps,
      onBack,
      onForward,
      forwardLabel,
      forwardDisabled,
      forwardInactive,
      hideForward,
      forwardBusy,
    }),
    [
      currentStep,
      totalSteps,
      onBack,
      onForward,
      forwardLabel,
      forwardDisabled,
      forwardInactive,
      hideForward,
      forwardBusy,
    ],
  );

  return (
    <OnboardingLayoutContextProvider value={value}>
      <Root onSubmit={handleSubmit} noValidate>
        {children}
      </Root>
    </OnboardingLayoutContextProvider>
  );
}
